import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import styles from "./Hero.module.sass";
import Image from "../../../components/Image";
import ScrollButton from "../../../components/ScrollButton";

const Hero = ({ scrollToRef }) => {
    const heroRef = useRef(null);
    const imageRef = useRef(null);
    const [scrollY, setScrollY] = useState(0);
    const [cursors, setCursors] = useState([
        { id: 1, x: 20, y: 30, image: 'cursor1.svg' },
        { id: 2, x: 60, y: 20, image: 'cursor2.svg' },
        { id: 3, x: 80, y: 50, image: 'cursor3.svg' }
    ]);

    useEffect(() => {
        const handleScroll = () => {
            if (heroRef.current && imageRef.current) {
                const rect = heroRef.current.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                
                // Calculate scroll progress (0 to 1) - Faster animation
                const totalScrollDistance = windowHeight + rect.height * 0.5;
                const currentScroll = windowHeight - rect.top;
                
                const progress = Math.max(0, Math.min(1, currentScroll / totalScrollDistance));
                
                setScrollY(progress);
            }
        };

        // Check if user prefers reduced motion
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isMobile = window.innerWidth <= 768;
        
        if (!prefersReducedMotion && !isMobile) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll(); // Initial call
        }

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Animate cursors randomly
    useEffect(() => {
        const animateCursors = () => {
            setCursors(prev => prev.map(cursor => ({
                ...cursor,
                x: Math.random() * 90 + 5, // 5% to 95% of container width
                y: Math.random() * 80 + 10  // 10% to 90% of container height
            })));
        };

        const interval = setInterval(animateCursors, 3000); // Move every 3 seconds
        return () => clearInterval(interval);
    }, []);

    // Calculate transform values based on scroll progress
    const perspectiveRotation = Math.max(0, 25 - (scrollY * 25)); // Start at 25deg, reduce to 0
    const translateY = scrollY * -40; // Move up as we scroll
    const scale = 1 + (scrollY * 0.04); // Scale increase
    const opacity = Math.max(0.7, 1 - (scrollY * 0.15)); // Fade but not too much

    return (
        <div 
            ref={heroRef}
            className={styles.hero}
        >
            <div className={cn("container", styles.container)}>
                <div className={styles.wrap}>
                    <div className={cn("stage", styles.stage)}>
                        Protection des données
                    </div>
                    <h1 className={cn("h1", styles.title)}>
                        Politique de <span className={styles.highlight}>Confidentialité</span>
                    </h1>
                    <div className={styles.text}>
                        Nous prenons soin de vos données. Découvrez comment nous collectons, utilisons et protégeons vos informations personnelles.
                    </div>
                    <div className={styles.info}>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Conforme RGPD :</span>
                            <span className={styles.infoValue}>✓ Oui</span>
                        </div>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Dernière mise à jour :</span>
                            <span className={styles.infoValue}>{new Date().toLocaleDateString('fr-FR')}</span>
                        </div>
                    </div>
                </div>
                <div className={styles.gallery}>
                    {/* Animated Cursors */}
                    {cursors.map(cursor => (
                        <div
                            key={cursor.id}
                            className={styles.cursor}
                            style={{
                                left: `${cursor.x}%`,
                                top: `${cursor.y}%`,
                                transform: 'translate(-50%, -50%)',
                                transition: 'all 3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            <img
                                src={`/images/${cursor.image}`}
                                alt="Cursor"
                                className={styles.cursorImage}
                            />
                        </div>
                    ))}
                    <div 
                        ref={imageRef}
                        className={styles.heroImageContainer}
                        style={{
                            transform: `
                                perspective(1000px) 
                                rotateX(${perspectiveRotation}deg) 
                                translateY(${translateY}px) 
                                scale(${scale})
                            `,
                            transformOrigin: 'center bottom',
                            transformStyle: 'preserve-3d',
                            opacity: opacity
                        }}
                    >
                        <Image
                            className={styles.heroImage}
                            src="/images/hero-pic-light.svg"
                            srcDark="/images/hero-pic-dark.svg"
                            alt="Hero Dashboard"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Hero;
